import * as cmAemFlexBatteryAdaptiveExpTypes from '../../consts/communicationLayer/cmAemFlexBatteryAdaptiveExpTypes';
import * as cmHapticProfileTypes from '../../consts/communicationLayer/cmHapticProfileTypes';
import * as cmOnOffTypes from '../../consts/communicationLayer/cmOnOffTypes';
import * as aemFlexBatteryModeTypes from '../../consts/iot/aemFlexBatteryModeTypes';
import * as autoStartModeTypes from '../../consts/iot/autoStartModeTypes';
import {FLEX_PUFF_ENGINE_JOULE_ENABLED} from '../../consts/iot/flexPuffEngineJouleTypes';
import * as iotParentProtectionStatus from '../../consts/iot/iotParentProtectionStatus';
import * as responsiveDrawModeTypes from '../../consts/iot/responsiveDrawModeTypes';
import * as yapResponsiveDrawModeTypes from '../../consts/yap/responsiveDrawModeTypes';
import * as yapUpdateSettingsTypes from '../../consts/yap/yapUpdateSettingsTypes';
import {selectIotDeviceData, selectIsMonoDevice} from '../../state/selectors/iotDevice';
import {getState} from '../../state/store';
import {amplitudeSettingsTracking} from '../amplitude';
import iotDeviceFWUStateService from '../iotDevice/iotDeviceFWUStateService';
import yapFWUService from '../yap/yapFWUService';
import gamClientService from './gamClientService';

const publishConnectionCareMessage = () => {};

const publishVibrationAlarmMessage = () => {};

const publishStopVibrationAlarmMessage = () => {};

const publishParentProtectionEnableMessage = async () => {
    await publishParentProtectionStatusMessage(iotParentProtectionStatus.PARENT_PROTECTION_ENABLED);
};

const publishParentProtectionDisableMessage = async () => {
    await publishParentProtectionStatusMessage(iotParentProtectionStatus.PARENT_PROTECTION_DISABLED);
};

const publishParentProtectionStatusMessage = async (protectionType) => {
    if (!protectionType) return;

    const isMonoDevice = selectIsMonoDevice(getState());

    const message = {
        settingName: yapUpdateSettingsTypes.PARENT_PROTECTION,
        newValue: {protectionType, timeout: null},
    };

    await gamClientService.updateGamSettings(message, !isMonoDevice);
};

const publishCheckFirmwareMessage = async (fwuTarget) => {
    iotDeviceFWUStateService.setIotTargetFwuStateOnFwuCheckRequest(fwuTarget);

    const result = await yapFWUService.getFirmwareAvailability(fwuTarget);

    if (!result) {
        iotDeviceFWUStateService.setIotTargetFwuStateOnFwuCheckRequest(fwuTarget, false);
    }
    return result;
};

const publishInitiateUpdateFirmwareMessage = async (fwuTarget) => {
    await yapFWUService.updateFirmware(fwuTarget, publishCheckFirmwareMessage);
};

const publishGetLedSettingsMessage = () => {};

const publishGetVibrationSettingsMessage = () => publishVibrationSettingsMessage();

const publishGetAutoStartSettingsMessage = () => publishAutoStartSettingsMessage();

const publishGetGestureSettingsMessage = () => publishGesturetSettingsMessage();

const publishGetResponsiveDrawSettingsMessage = () => publishResponsiveDrawSettingsMessage();

const publishGetVapeCloudSizeSettingsMessage = () => publishVapeCloudSizeSettingsMessage();

const publishGetCountdownSettingsMessage = () => publishCountdownSettingsMessage();

const publishSetLedSettingsMessage = async (mode, isDeviceLedModeEnabled, isHolderLedModeEnabled) => {
    if (!mode) return;

    const message = {
        settingName: yapUpdateSettingsTypes.LED_INTENSITY,
        newValue: {intensity: mode},
    };
    const iotDevice = selectIotDeviceData(getState());
    const {holder, device} = iotDevice?.ledData || {};
    const holderLed = holder?.intensity;
    const deviceLed = device?.intensity;

    if (isDeviceLedModeEnabled && mode !== deviceLed) {
        amplitudeSettingsTracking.trackIllumination({mode});
        await gamClientService.updateGamSettings(message);
    }

    if (isHolderLedModeEnabled && mode !== holderLed) {
        amplitudeSettingsTracking.trackIllumination({mode, isHolder: true});
        await gamClientService.updateGamSettings(message, true);
    }
};

const publishSetResponsiveDrawSettingsMessage = (mode) => publishResponsiveDrawSettingsMessage(mode);

const publishSetVapeCloudSizeSettingsMessage = (mode) => publishVapeCloudSizeSettingsMessage(mode);

const publishSetVibrationSettingsMessage = async (vibrationData, settings, isMonoDevice, isP1V4) => {
    if (!vibrationData) return;

    let message = null;
    let isHolder = !isMonoDevice;

    if (vibrationData.all_holder_vibrations !== undefined) {
        message = {
            settingName: yapUpdateSettingsTypes.HEATING_VIBRATION,
            newValue: {
                hapticDelay: 1,
                hapticProfile: vibrationData.all_holder_vibrations
                    ? cmHapticProfileTypes.ENABLED
                    : cmHapticProfileTypes.DISABLED,
            },
        };
    } else if (vibrationData.start_heating !== undefined) {
        if (isMonoDevice && isP1V4) {
            //P1V4 DELTA only
            message = {
                settingName: yapUpdateSettingsTypes.UI_HEATING_VIBRATION,
                newValue: {
                    preHeatVibration: vibrationData.start_heating ? 1 : 0,
                    heatingVibration: vibrationData.fully_heated ? 1 : 0,
                    nearEndHeatVibration: vibrationData.almost_finished ? 1 : 0,
                    heatingEndVibration: vibrationData.finished ? 1 : 0,
                    endChargeVibration: 0,
                },
            };
        } else {
            message = {
                settingName: yapUpdateSettingsTypes.HEATING_VIBRATION,
                newValue: {
                    startHeating: vibrationData.start_heating,
                    fullyHeated: vibrationData.fully_heated,
                    nearEnd: vibrationData.almost_finished,
                    endOfHeating: vibrationData.finished,
                    hapticDelay: 0,
                    hapticProfile: cmHapticProfileTypes.PROGRAMMABLE,
                },
            };
        }
    } else if (vibrationData.battery_charged !== undefined) {
        isHolder = false;
        message = {
            settingName: yapUpdateSettingsTypes.BATTERY_CHARGED_VIBRATION,
            newValue: {batteryCharged: vibrationData.battery_charged},
        };
    } else if (vibrationData.start_charging !== undefined) {
        const onOffMessage = vibrationData.start_charging ? cmOnOffTypes.ON : cmOnOffTypes.OFF;

        message = {
            settingName: yapUpdateSettingsTypes.UI_BATTERY_CHARGED_VIBRATION,
            newValue: {batteryChargedOneExp: onOffMessage, batteryChargedTwoExp: onOffMessage},
        };
    } else if (vibrationData.extended_haptic_profile !== undefined) {
        const durations = [
            {
                durationIsOn: vibrationData.extended_haptic_profile,
                durationOfPhase: 10,
            },
        ];

        for (let i = 2; i < 14; i++) {
            durations.push({
                durationIsOn: false,
                durationOfPhase: 0,
            });
        }

        message = {
            settingName: yapUpdateSettingsTypes.EXT_HAPTIC_PROFILE,
            newValue: {
                feature: 0,
                durations,
            },
        };
    }

    if (message) {
        amplitudeSettingsTracking.trackVibration(settings);
        await gamClientService.updateGamSettings(message, isHolder);
    }
};

const publishSetAutoStartSettingsMessage = async (autostartFeatureState) => {
    const onOffMessage =
        autostartFeatureState === autoStartModeTypes.AUTO_START_STATE_ENABLED ? cmOnOffTypes.ON : cmOnOffTypes.OFF;
    const message = {
        settingName: yapUpdateSettingsTypes.AUTO_START_HEATING,
        newValue: {featureState: onOffMessage},
    };
    const isMonoDevice = selectIsMonoDevice(getState());

    await gamClientService.updateGamSettings(message, !isMonoDevice);
};

const publishSetAemFlexBatteryAdaptiveExpSettingsMessage = async (aemFlexBatteryValue) => {
    const adaptiveExpValue =
        aemFlexBatteryValue === aemFlexBatteryModeTypes.ECO_MODE
            ? cmAemFlexBatteryAdaptiveExpTypes.ECO_MODE
            : cmAemFlexBatteryAdaptiveExpTypes.PERFORMANCE_MODE;
    const message = {
        settingName: yapUpdateSettingsTypes.AEM_FLEX_BATTERY_ADAPTIVE_EXP,
        newValue: {mode: adaptiveExpValue},
    };
    const isMonoDevice = selectIsMonoDevice(getState());

    await gamClientService.updateGamSettings(message, !isMonoDevice);
};

const publishSetAemFlexBatteryHeatingPauseSettingsMessage = async (isPauseOn) => {
    const onOffMessage = isPauseOn ? cmOnOffTypes.ON : cmOnOffTypes.OFF;
    const message = {
        settingName: yapUpdateSettingsTypes.AEM_FLEX_BATTERY_PAUSE_HEATING,
        newValue: {featureState: onOffMessage},
    };
    const isMonoDevice = selectIsMonoDevice(getState());

    await gamClientService.updateGamSettings(message, !isMonoDevice);
};

const publishSetFlexPuffEngineJouleSettingsMessage = async (flexPuffValue) => {
    const onOffMessage = flexPuffValue === FLEX_PUFF_ENGINE_JOULE_ENABLED ? cmOnOffTypes.ON : cmOnOffTypes.OFF;
    const message = {
        settingName: yapUpdateSettingsTypes.FLEX_PUFF_ENGINE_JOULE,
        newValue: {featureState: onOffMessage},
    };
    const isMonoDevice = selectIsMonoDevice(getState());

    await gamClientService.updateGamSettings(message, !isMonoDevice);
};

const publishSetGestureSettingsMessage = async (data) => {
    const {status_gesture_pickup_enabled, status_gesture_double_tap_enabled} = data?.holder || {};

    let message = null;
    const getGestureState = (settingName, onOffMessage) => {
        return {settingName, newValue: {gestureState: onOffMessage}};
    };

    if (status_gesture_pickup_enabled !== undefined) {
        const onOffMessage = status_gesture_pickup_enabled ? cmOnOffTypes.ON : cmOnOffTypes.OFF;

        message = getGestureState(yapUpdateSettingsTypes.LIFT_UP_GESTURE, onOffMessage);
    }

    if (status_gesture_double_tap_enabled !== undefined) {
        const onOffMessage = status_gesture_double_tap_enabled ? cmOnOffTypes.ON : cmOnOffTypes.OFF;

        message = getGestureState(yapUpdateSettingsTypes.DOUBLE_TAP_GESTURE, onOffMessage);
    }

    if (message) {
        const isMonoDevice = selectIsMonoDevice(getState());

        await gamClientService.updateGamSettings(message, !isMonoDevice);
    }
};

const publishSetCountdownSettingsMessage = () => publishCountdownSettingsMessage();

const publishResetVibrationSettingsMessage = () => publishVibrationSettingsMessage();

const publishResponsiveDrawSettingsMessage = (mode) => {
    const message = {
        settingName: yapUpdateSettingsTypes.RESPONSIVE_DRAW_PROFILE,
    };

    if (mode) {
        if (mode === responsiveDrawModeTypes.VIVACE) {
            mode = yapResponsiveDrawModeTypes.ALLEGRO;
        } else if (mode === responsiveDrawModeTypes.STACCATO) {
            mode = yapResponsiveDrawModeTypes.VIVALDI;
        }
        message.newValue = {hapticPattern: mode};
    }

    return gamClientService.updateGamSettings(message);
};

const publishVapeCloudSizeSettingsMessage = (mode) => {
    const message = {
        settingName: yapUpdateSettingsTypes.VAPE_CLOUD_SIZE,
    };

    if (mode) {
        message.newValue = {heatingProfile: mode};
    }

    return gamClientService.updateGamSettings(message);
};

const publishSessionProfilingSettingsMessage = async (puffData) => {
    const {puffNumber, puffingTime} = puffData || {};

    const message = {
        settingName: yapUpdateSettingsTypes.SESSION_PROFILING,
        newValue: {puffNumber, puffingTime},
    };

    await gamClientService.updateGamSettings(message);
};

const publishVibrationSettingsMessage = () => {};

const publishAutoStartSettingsMessage = () => {};

const publishGesturetSettingsMessage = () => {};

const publishCleaningMessage = () => {};

const publishCleaningCheckMessage = () => {};

const publishCountdownSettingsMessage = () => {};

const publishDeviceRestartMessage = async (isHolder) => {
    return gamClientService.resetMyDevice(isHolder);
};

const publishBatteryInfoCharacteristicUsbMessage = () => {};

export default {
    publishCheckFirmwareMessage,
    publishCleaningMessage,
    publishConnectionCareMessage,
    publishGetLedSettingsMessage,
    publishGetVibrationSettingsMessage,
    publishGetAutoStartSettingsMessage,
    publishGetGestureSettingsMessage,
    publishGetCountdownSettingsMessage,
    publishCleaningCheckMessage,
    publishParentProtectionDisableMessage,
    publishParentProtectionEnableMessage,
    publishParentProtectionStatusMessage,
    publishResetVibrationSettingsMessage,
    publishSetLedSettingsMessage,
    publishSetVibrationSettingsMessage,
    publishSetAutoStartSettingsMessage,
    publishSetFlexPuffEngineJouleSettingsMessage,
    publishSetAemFlexBatteryAdaptiveExpSettingsMessage,
    publishSetAemFlexBatteryHeatingPauseSettingsMessage,
    publishSetGestureSettingsMessage,
    publishSetCountdownSettingsMessage,
    publishInitiateUpdateFirmwareMessage,
    publishStopVibrationAlarmMessage,
    publishVibrationAlarmMessage,
    publishGetResponsiveDrawSettingsMessage,
    publishSetResponsiveDrawSettingsMessage,
    publishGetVapeCloudSizeSettingsMessage,
    publishSessionProfilingSettingsMessage,
    publishSetVapeCloudSizeSettingsMessage,
    publishDeviceRestartMessage,
    publishBatteryInfoCharacteristicUsbMessage,
};
